<template>
  <div class="card flex flex-column align-item-start">
    <div class="report-parameters-text">
      <p>
        Анализирует результаты тестирования выбранной группы: средние показатели по всем метрикам,
        распределения по уровням развития и ключевые сильные/слабые зоны.
      </p>
    </div>

    <div class="report-options__body">
      <div>
        <div class="label">
          Группа
        </div>

        <a-select
            v-model:value="selectedGroup"
            :placeholder="'Выберите группу'"
            style="width: 100%;"
            :not-found-content="'Нет данных'"
            show-arrow
        >
          <a-select-option
              v-for="item in groupOptions"
              :key="item.id"
              :value="item.id"
          >
            {{ item.text }}
          </a-select-option>
        </a-select>
      </div>

      <div>
        <div class="label">
          Дата оценки
        </div>

        <a-date-picker
            v-model:value="assessmentDate"
            type="date"
            placeholder="Выберите дату"
            format="DD.MM.YYYY"
            value-format="YYYY-MM-DD"
            style="width: 100%"
        />
      </div>
    </div>

    <div class="report-options__btn flex justify-content-center">
      <a-button
          @click="requestForReport"
          class="primary-button"
          :disabled="!canGenerateReport"
      >
        Сформировать отчет
      </a-button>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeMount, onBeforeUnmount, ref } from "@vue/runtime-core";
import { useStore } from "vuex";
import { message } from "ant-design-vue";
import { getReportDetailsEmployeeCountText } from "./reportDetailsEmployeeCount";

export default {
  name: "group-ei-profile-parameters",

  setup(props, context) {
    const store = useStore();

    const selectedGroup = ref(null);
    const groupOptions = ref([]);
    const assessmentDate = ref(null);
    const lastGeneratedPayload = ref(null);

    const groups = computed(() => store.state.group.groups);
    const user = computed(() => store.state.profile.profile);

    const isReadyForReport = computed(() => {
      return selectedGroup.value !== null && assessmentDate.value;
    });

    onBeforeMount(() => {
      store.dispatch("group/getGroups").then(() => {
        groupOptions.value = [
          {
            id: 0,
            text: "Все сотрудники",
            users_count: null,
          },
          ...groups.value.map((group) => {
            return {
              id: group.id,
              text: group.name,
              users_count: group.users_count,
            };
          }),
        ];
      });
    });

    const formatDateForDisplay = (value) => {
      if (!value) {
        return null;
      }

      const [year, month, day] = value.split("-");

      return `${day}.${month}.${year}`;
    };

    const parseBackendDate = (value) => {
      if (!value) {
        return null;
      }

      const [year, month, day] = value.split("-");

      return new Date(Number(year), Number(month) - 1, Number(day));
    };

    const getSelectedGroupObject = (groupId) => {
      if (groupId === 0) {
        return {
          id: 0,
          text: "Все сотрудники",
          users_count: null,
        };
      }

      const foundGroup = groups.value.find((item) => item.id === groupId);

      if (!foundGroup) {
        return {
          id: 0,
          text: "Все сотрудники",
          users_count: null,
        };
      }

      return {
        id: foundGroup.id,
        text: foundGroup.name,
        users_count: foundGroup.users_count,
      };
    };

    const getPayload = () => {
      const groupId = selectedGroup.value === 0 ? null : selectedGroup.value;

      const payload = {
        group_id: groupId,
        date: assessmentDate.value,
      };

      if (groupId === null) {
        payload.company_id = user.value?.company?.id ?? null;
      }

      return payload;
    };

    const getSelectedGroupCount = (group) => {
      return group.id === 0 ? "all" : group.users_count;
    };

    const serializePayload = (payload) => {
      return JSON.stringify(payload);
    };

    const isReportAlreadyGenerated = computed(() => {
      if (!lastGeneratedPayload.value) {
        return false;
      }

      return lastGeneratedPayload.value === serializePayload(getPayload());
    });

    const canGenerateReport = computed(() => {
      return isReadyForReport.value && !isReportAlreadyGenerated.value;
    });

    const requestForReport = () => {
      if (!isReadyForReport.value) {
        return;
      }

      const parsedAssessmentDate = parseBackendDate(assessmentDate.value);

      if (!parsedAssessmentDate) {
        message.error("Выберите дату");
        return;
      }

      const payload = getPayload();

      store.dispatch("group/clearAnalyticsGroupData");

      const selectedGroupObject = getSelectedGroupObject(selectedGroup.value);
      const dateString = formatDateForDisplay(assessmentDate.value);

      store.dispatch("group/setAnalyticsGroupData", {
        group_1: {
          name: selectedGroupObject.text,
          count: selectedGroupObject.users_count ?? null,
          dateInterval: dateString,
        },
        group_2: null,
      });

      const reportDetails = {
        groupName: selectedGroupObject.text,
        filterName: "",
        employeeCount: getReportDetailsEmployeeCountText(getSelectedGroupCount(selectedGroupObject)),
        dateString,
        dateInterval: dateString,
        type: "group",
      };

      store.commit("analytics/setCurrentReportDetails", reportDetails);

      lastGeneratedPayload.value = serializePayload(payload);

      context.emit("reportRequest", payload);
    };

    onBeforeUnmount(() => {
      store.commit("analytics/clearCurrentReportDetails");
    });

    return {
      selectedGroup,
      groupOptions,
      groups,
      assessmentDate,
      isReadyForReport,
      canGenerateReport,
      requestForReport,
    };
  },
};
</script>

<style scoped lang="scss">
.card {
  p {
    padding-left: 0;
    font-size: 14px;
  }
}

.label {
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.report-options__body {
  display: grid;
  grid-template-columns: 400px 180px;
  column-gap: 10px;
  row-gap: 12px;
  max-width: 800px;
}

.report-parameters-text {
  margin-bottom: 16px;
  max-width: 800px;

  p {
    margin: 0;
  }
}

@media (max-width: $mobile-breakpoint) {
  .report-options__btn,
  .primary-button {
    width: 100%;
  }

  .report-options__body {
    width: 100%;
    grid-template-columns: 1fr;
  }
}
</style>
